import Utils from "context/utils";

export default class SessionManager {
  constructor(app, revision, updateProgress, onUpdate) {
    this.app = app;
    this.revision = revision;
    this.updateProgress = updateProgress;
    this.listeners = new Set();
    if (onUpdate) {
      this.listeners.add(onUpdate);
    }

    // Session state
    this.sessions = null;
    this.sessionId = null;
    this.session = null;
    this.sessionObjects = null;
    this.sessionData = {};
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.loadingSessionObjects = new Set();
    
    // Sync state
    this.sessionSync = false;
    this.sessionSyncCounter = 0;
    this.lastSessionId = null;

    // Subscriptions
    this.subscriptions = {};
    
    // Timer for auto-refresh
    this.mounted = true;
    this.autoRefreshTimer = null;
  }

  destroy() {
    this.mounted = false;
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
  }

  /**
   * Subscriptions
   */

  subscribe(alias, callback) {
    if (!this.subscriptions[alias]) {
      this.subscriptions[alias] = [];
    }
    this.subscriptions[alias].push(callback);
    
    // If data is already available, notify immediately
    if (this.sessionData[alias]) {
      callback(this.sessionData[alias]);
    }

    return () => {
      this.unsubscribe(alias, callback);
    }
  }

  unsubscribe(alias, callback) {
    if (!this.subscriptions[alias]) {
      return;
    }
    this.subscriptions[alias] = this.subscriptions[alias].filter(cb => cb !== callback);
  }

  notify(alias, value) {
    if (!this.subscriptions[alias]) {
      return;
    }
    for (const callback of this.subscriptions[alias]) {
      try {
        callback(value);
      } catch (e) {
        console.error(`Error notifying subscription for ${alias}:`, e);
      }
    }
  }

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  notifyListeners() {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (e) {
        console.error("Error notifying listener:", e);
      }
    }
  }

  /**
   * Data Access
   */

  getSessionData(alias) {
    return this.sessionData[alias];
  }

  /**
   * Actions
   */

  clearSessionState(session) {
    this.sessionId = session["id"];
    this.session = session;
    this.sessionObjects = null;
    this.sessionData = {};
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.loadingSessionObjects.clear();
  }

  loadSessions(more) {
    this.updateProgress(true, more ? "Loading more sessions..." : "Loading sessions...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "list_sessions"
    };

    if (more) {
      body["start_session_id"] = this.lastSessionId;
    }

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        let sessions = response.data.sessions;
        this.lastSessionId = response.data.last_session_id;

        if (more) {
          this.sessions = (this.sessions || []).concat(sessions);
        } else {
            this.sessions = sessions;
        }
        
        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();

        return this.sessions;
      }, error => {
        this.updateProgress(false, null, true, "Failed to load sessions: " + error.message);
      });
  }

  loadSessionContent() {
    const body = {
      "engine": "workflow_blobhub",
      "command": "list_session_objects",
      "session_id": this.sessionId
    };
    
    const requestedSessionId = this.sessionId;

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        this.sessionObjects = (response.data["objects"] || []).map(obj => {
          if (obj.updated_at) {
            obj.updatedAt = obj.updated_at;
            delete obj.updated_at;
          }
          return obj;
        });
        
        for (const object of this.sessionObjects) {
          this.loadSessionObject(object["alias"]);
        }
        this.notifyListeners();
      }, error => {
        console.error("Failed to load session content: " + error.message);
      });
  }

  loadSessionObject(alias) {
    const body = {
      "engine": "workflow_blobhub",
      "command": "download_session_object",
      "session_id": this.sessionId,
      "alias": alias
    };
    
    this.loadingSessionObjects.add(alias);
    this.notifyListeners();

    const requestedSessionId = this.sessionId;

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const object = response.data["object"];
        const value = object["value"];
        this.sessionData[alias] = value;
        
        // Update session object metadata
        if (this.sessionObjects) {
          const sessionObject = this.sessionObjects.find(obj => obj.alias === alias);
          if (sessionObject) {
            sessionObject.updatedAt = object.updated_at;
          } else {
            this.sessionObjects.push({
              alias: alias,
              updatedAt: object.updated_at
            });
            this.sessionObjects.sort((a, b) => a.alias.localeCompare(b.alias));
          }
        }

        this.loadingSessionObjects.delete(alias);
        this.notify(alias, value);
        this.notifyListeners();
      }, error => {
        console.error(`Failed to load session object ${alias}: ` + error.message);
        this.loadingSessionObjects.delete(alias);
        this.notifyListeners();
      });
  }

  autoRefreshSessionEvents() {
    if (!this.mounted) return;

    this.autoRefreshTimer = setTimeout(() => {
      if (!this.mounted) return;

      if (this.sessionSync) {
          if (this.sessionSyncCounter > 180) {
              this.enableSessionSync(false);
          } else {
              this.sessionSyncCounter++;
          }
      }

      if (this.sessionSync && this.session && this.session["status"] === "open") {
          this.loadRecentSessionEvents();
      }

      this.autoRefreshSessionEvents();
    }, 1000);
  }

  enableSessionSync(enable) {
    this.sessionSync = enable;
    this.sessionSyncCounter = 0;

    if (enable) {
      this.sessionEventCreatedAt = new Date().toISOString();
      
      // NEXT: This does the job but it is not optimal and causes unnecessary UX turmoil with views reloading.
      // Ideally we should only load session objects that have changed since the last sync.
      // this.loadSessionContent();
    }

    this.notifyListeners();
  }

  loadRecentSessionEvents() {
    let createdSince = this.sessionEventCreatedAt;
    this.loadSessionEvents(createdSince, true);
  }

  loadSessionEvents(createdSince, ascending) {
    const body = {
      "engine": "workflow_blobhub",
      "command": "list_session_events",
      "session_id": this.sessionId,
      "include_session": true,
      "ascending": ascending
    };

    if (createdSince) {
      body["created_since"] = createdSince;
    }

    const requestedSessionId = this.sessionId;

    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const session = response.data["session"];
        const sessionEvents = response.data["session_events"];

        this.session = session;
        this.sessionEvents = sessionEvents;

        if (sessionEvents.length > 0) {
            this.sessionEventCreatedAt = sessionEvents[sessionEvents.length - 1]["created_at"];
        }

        this.refreshSessionObjects(sessionEvents);
      }, error => {
        console.error("Failed to load session events: " + error.message);
      });
  }

  refreshSessionObjects(sessionEvents) {
    const aliasesToRefresh = new Set();
    let hasDeletions = false;

    for (const sessionEvent of sessionEvents) {
      if ("session_object_modified" === sessionEvent["type"]) {
        aliasesToRefresh.add(sessionEvent["session_object"]["alias"]);
      } else if ("session_object_deleted" === sessionEvent["type"]) {
        const alias = sessionEvent["session_object"]["alias"];
        
        if (this.sessionObjects) {
          const initialLength = this.sessionObjects.length;
          this.sessionObjects = this.sessionObjects.filter(obj => obj.alias !== alias);
          if (this.sessionObjects.length !== initialLength) hasDeletions = true;
        }
        
        if (alias in this.sessionData) {
            delete this.sessionData[alias];
            hasDeletions = true;
        }
        
        // If deleted, don't refresh even if it was modified in the same batch (unlikely but safe)
        aliasesToRefresh.delete(alias);
      }
    }

    for (const alias of aliasesToRefresh) {
      this.loadSessionObject(alias);
    }

    if (hasDeletions) {
      this.notifyListeners();
    }
  }

  createNewSession(session) {
    this.updateProgress(true, "Creating a new session...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "create_session"
    };

    if (session) {
      if (session.description && session.description.trim()) {
        body["description"] = session.description.trim();
      }
      if (session.tracing) {
        body["tracing"] = session.tracing;
      }
    }

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        const newSession = response.data["session"];
        this.sessions = [newSession].concat(this.sessions || []);
        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();
        return newSession;
      }, error => {
        this.updateProgress(false, null, true, "Failed to create new session: " + error.message);
      });
  }

  uploadSessionObject(alias, value) {
    this.updateProgress(true, "Updating session object...", false, null);
    
    const body = {
      engine: "workflow_blobhub",
      command: "upload_session_object",
      session_id: this.sessionId,
      alias: alias,
      value: value
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        this.sessionData[alias] = value;
        
        // Update session objects list
        if (this.sessionObjects) {
          const existingIndex = this.sessionObjects.findIndex(obj => obj.alias === alias);
          if (existingIndex !== -1) {
            // Update existing
            this.sessionObjects[existingIndex].updatedAt = new Date().toISOString();
          } else {
            // Add new
            this.sessionObjects.push({
              alias: alias,
              updatedAt: new Date().toISOString()
            });
            // Sort by alias
            this.sessionObjects.sort((a, b) => a.alias.localeCompare(b.alias));
          }
        }

        this.notify(alias, value);
        this.notifyListeners();
      }, error => {
        this.updateProgress(false, null, true, "Failed to update session object: " + error.message);
      });
  }

  deleteSessionObject(alias) {
    this.updateProgress(true, "Deleting session object...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "delete_session_object",
      session_id: this.sessionId,
      alias: alias
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        
        // Remove from session objects list
        if (this.sessionObjects) {
          this.sessionObjects = this.sessionObjects.filter(obj => obj.alias !== alias);
        }
        
        // Remove data
        delete this.sessionData[alias];
        
        // Notify listeners
        this.notifyListeners();
      }, error => {
        this.updateProgress(false, null, true, "Failed to delete session object: " + error.message);
      });
  }
}
